import { callOpenAIChat } from './cloudFunctions';

// OpenAI Service voor SuperKonnected
// API calls gaan via Firebase Cloud Functions — de echte API key is nooit zichtbaar in de browser.
export class OpenAIService {

  isConfigured(): boolean {
    // Altijd beschikbaar: de key zit server-side in Google Secret Manager
    return true;
  }

  async sendMessage(message: string, context?: string): Promise<string> {
    try {
      const isIntentRequest = context && context.includes('STRIKT JSON format');

      const systemPrompt = isIntentRequest
        ? `${context}`
        : `Je bent een AI assistent voor SuperKonnected, een community platform voor ondernemers en podcasters. 
      
Je helpt gebruikers met:
- Podcast strategie en content planning
- Netwerken en community building
- Ondernemerschap en business development
- Technische vragen over het platform

Antwoord altijd in het Nederlands, behalve als de gebruiker specifiek om een andere taal vraagt.
Wees behulpzaam, professioneel en geef praktische adviezen.

${context ? `Context: ${context}` : ''}`;

      return await callOpenAIChat({
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: message },
        ],
        model: isIntentRequest ? 'gpt-4o-mini' : 'gpt-4o-mini',
        maxTokens: isIntentRequest ? 50 : 1000,
        temperature: isIntentRequest ? 0 : 0.7,
      });
    } catch (error) {
      console.error('OpenAI Error:', error);
      throw new Error('Er is een fout opgetreden bij het communiceren met de AI assistent.');
    }
  }

  // Streaming is niet beschikbaar via Cloud Functions; we simuleren het met één call
  async sendMessageStream(message: string, context?: string, onChunk?: (chunk: string) => void): Promise<string> {
    const systemPrompt = `Je bent een AI assistent voor SuperKonnected, een community platform voor ondernemers en podcasters. 
      
Je helpt gebruikers met:
- Podcast strategie en content planning
- Netwerken en community building
- Ondernemerschap en business development
- Technische vragen over het platform

Antwoord altijd in het Nederlands, behalve als de gebruiker specifiek om een andere taal vraagt.
Wees behulpzaam, professioneel en geef praktische adviezen.

${context ? `Context: ${context}` : ''}`;

    try {
      const fullResponse = await callOpenAIChat({
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: message },
        ],
        model: 'gpt-4o-mini',
        maxTokens: 1000,
        temperature: 0.7,
      });

      // Simuleer streaming door tekst per woord te sturen
      if (onChunk) {
        const words = fullResponse.split(' ');
        for (const word of words) {
          onChunk(word + ' ');
          await new Promise(resolve => setTimeout(resolve, 20));
        }
      }

      return fullResponse;
    } catch (error) {
      console.error('OpenAI Error:', error);
      throw new Error('Er is een fout opgetreden bij het communiceren met de AI assistent.');
    }
  }

  // Podcast strategie suggesties
  async getPodcastStrategySuggestions(topic: string, audience: string): Promise<string> {
    const prompt = `Geef me 5 concrete strategieën voor een podcast over "${topic}" gericht op "${audience}". 
    Elke strategie moet bevatten:
    - Titel idee
    - Content focus
    - Gasten suggesties
    - Marketing tactieken
    - Verwachtte uitkomst`;
    
    return this.sendMessage(prompt);
  }

  // Content ideeën genereren
  async generateContentIdeas(podcastTheme: string, episodeCount: number = 10): Promise<string> {
    const prompt = `Genereer ${episodeCount} podcast aflevering ideeën voor een show over "${podcastTheme}".
    Voor elke aflevering geef:
    - Titel
    - Korte beschrijving
    - Belangrijkste punten
    - Type gast (indien relevant)
    - Geschatte duur`;
    
    return this.sendMessage(prompt);
  }

  // Netwerk tips
  async getNetworkingTips(context: string): Promise<string> {
    const prompt = `Geef me praktische netwerk tips voor: ${context}
    Focus op:
    - Online netwerken
    - Community building
    - Authentieke connecties
    - Follow-up strategieën`;
    
    return this.sendMessage(prompt);
  }

  async generateCompletion(messages: Array<{role: 'system' | 'user' | 'assistant', content: string}>): Promise<{success: boolean, content?: string, error?: string}> {
    try {
      const content = await callOpenAIChat({
        messages,
        model: 'gpt-4o-mini',
        maxTokens: 500,
        temperature: 0.7,
      });

      if (!content) {
        return { success: false, error: 'Geen antwoord ontvangen van AI' };
      }

      return { success: true, content };
    } catch (error: any) {
      console.error('OpenAI Error:', error);
      return { success: false, error: error.message || 'Er is een fout opgetreden bij het communiceren met de AI assistent.' };
    }
  }

  /**
   * Transcribe an audio/video blob using OpenAI Whisper API.
   * Upload het bestand naar Firebase Storage en gebruik een Cloud Function als proxy.
   * Tijdelijk: upload direct via FormData naar de Whisper endpoint (vereist REACT_APP_OPENAI_API_KEY).
   * TODO: vervang door een Cloud Function die het bestand uit Storage transcribeert.
   */
  async transcribeAudioBlob(blob: Blob): Promise<string> {
    const apiKey = process.env.REACT_APP_OPENAI_WHISPER_KEY || '';
    if (!apiKey) {
      console.warn('[transcribeAudioBlob] Whisper key niet geconfigureerd');
      return '';
    }

    const MAX_BYTES = 24 * 1024 * 1024;

    const transcribeChunk = async (chunk: Blob, index: number): Promise<string> => {
      try {
        const ext = chunk.type.includes('mp4') ? 'mp4' : 'webm';
        const formData = new FormData();
        formData.append('file', chunk, `chunk_${index}.${ext}`);
        formData.append('model', 'whisper-1');
        formData.append('language', 'nl');

        const response = await fetch('https://api.openai.com/v1/audio/transcriptions', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${apiKey}` },
          body: formData,
        });

        if (!response.ok) throw new Error(`Whisper HTTP ${response.status}`);
        const data = await response.json();
        return data.text ?? '';
      } catch (err) {
        console.error(`[transcribeAudioBlob] Chunk ${index} mislukt:`, err);
        return '';
      }
    };

    if (blob.size <= MAX_BYTES) {
      return transcribeChunk(blob, 0);
    }

    console.log(`[Whisper] Bestand is ${(blob.size / 1024 / 1024).toFixed(1)} MB — splitsen in chunks van 24 MB`);
    const parts: string[] = [];
    let offset = 0;
    let idx = 0;
    while (offset < blob.size) {
      const chunk = blob.slice(offset, offset + MAX_BYTES, blob.type);
      const text = await transcribeChunk(chunk, idx);
      parts.push(text);
      offset += MAX_BYTES;
      idx++;
    }
    return parts.filter(Boolean).join(' ');
  }

  /**
   * Generate structured meeting notes from a Whisper transcript.
   * Returns samenvatting, actiepunten, beslissingen, bullet points, score.
   */
  async generateMeetingNotes(
    transcript: string,
    entityName?: string,
  ): Promise<MeetingNotes> {
    const empty: MeetingNotes = {
      samenvatting: '',
      bulletPoints: [],
      actiepunten: [],
      beslissingen: [],
      leerpunten: [],
      score: 0,
    };

    if (!transcript.trim()) return empty;

    const context = entityName
      ? `Dit is een mastermind sessie van "${entityName}". Leden delen persoonlijke groei-uitdagingen, zakelijke vraagstukken en ervaringen met als doel elkaar te helpen groeien.`
      : 'Dit is een zakelijke meeting.';

    const prompt = `${context}

Analyseer het volgende transcript en geef een volledige, gestructureerde samenvatting in het Nederlands. Verwerk alle besproken inhoud — ook als het gesprek kort of rommelig klinkt.

TRANSCRIPT:
${transcript.slice(0, 12000)}

Geef je antwoord als geldig JSON in dit exacte formaat:
{
  "samenvatting": "2-4 zinnen heldere samenvatting van wat er besproken is",
  "bulletPoints": ["besproken onderwerp 1", "besproken onderwerp 2", "besproken onderwerp 3"],
  "actiepunten": ["actie 1 – eigenaar indien bekend", "actie 2"],
  "beslissingen": ["beslissing 1", "beslissing 2"],
  "leerpunten": ["leerpunt of inzicht 1", "leerpunt of inzicht 2"],
  "score": 7
}

Regels:
- samenvatting: 2-4 duidelijke zinnen, geen transcript-stijl
- bulletPoints: 3-6 besproken onderwerpen of key insights
- actiepunten: concrete taken, wie en wanneer indien hoorbaar
- beslissingen: wat is er afgesproken of beslist
- leerpunten: persoonlijke of zakelijke inzichten en lessen die uit de sessie komen
- score: 1-10 beoordeling van de sessiekwaliteit (1 bij te weinig inhoud)
- Als het transcript ruis of muziek bevat, geef dan score 1 en lege lijsten`;

    try {
      const raw = await callOpenAIChat({
        messages: [
          { role: 'system', content: 'Je bent een expert in meeting analyse. Antwoord altijd met geldig JSON.' },
          { role: 'user', content: prompt },
        ],
        model: 'gpt-4o-mini',
        maxTokens: 1000,
        temperature: 0.3,
        responseFormat: 'json_object',
      });

      const parsed = JSON.parse(raw);

      return {
        samenvatting: parsed.samenvatting ?? '',
        bulletPoints: Array.isArray(parsed.bulletPoints) ? parsed.bulletPoints : [],
        actiepunten: Array.isArray(parsed.actiepunten) ? parsed.actiepunten : [],
        beslissingen: Array.isArray(parsed.beslissingen) ? parsed.beslissingen : [],
        leerpunten: Array.isArray(parsed.leerpunten) ? parsed.leerpunten : [],
        score: typeof parsed.score === 'number' ? parsed.score : 0,
      };
    } catch (err) {
      console.error('[generateMeetingNotes] Error:', err);
      return empty;
    }
  }

  /**
   * Analyse post-tekst en geef relevante hashtag-suggesties terug.
   */
  async suggestHashtagsFromContent(text: string, collaborationNames: string[] = []): Promise<string[]> {
    const trimmed = text.trim();
    if (trimmed.length < 40) return [];

    const collabContext = collaborationNames.length > 0
      ? `De post is gelinkt aan samenwerkingen: ${collaborationNames.join(', ')}.`
      : '';

    const prompt = `Je analyseert de onderstaande post-tekst voor een sociaal platform gericht op samenwerken en communities.

${collabContext}

Geef 5 tot 8 relevante hashtags die de onderwerpen, thema's en context van de tekst goed weergeven.
- Geen hashtags voor triviale of loze woorden
- Gebruik bestaande, gangbare hashtags (enkelvoud of camelCase, geen spaties)
- Mix van brede (#Technologie) en specifieke (#Auteursrecht) tags
- Als de tekst placeholder- of nonsenstekst is (bijv. lorem ipsum), geef dan een lege array terug

Antwoord uitsluitend met geldig JSON: een array van strings zonder #-teken.
Voorbeeld: ["Technologie","Auteursrecht","Kunst","AI","Creativiteit"]

Tekst:
${trimmed.slice(0, 2000)}`;

    try {
      const raw = await callOpenAIChat({
        messages: [
          { role: 'system', content: 'Je bent een hashtag-expert. Antwoord altijd met een geldig JSON-array van strings.' },
          { role: 'user', content: prompt },
        ],
        model: 'gpt-4o-mini',
        maxTokens: 120,
        temperature: 0.3,
        responseFormat: 'json_object',
      });

      const parsed = JSON.parse(raw);
      const tags: unknown = Array.isArray(parsed) ? parsed : (parsed.hashtags ?? parsed.tags ?? Object.values(parsed)[0]);
      if (!Array.isArray(tags)) return [];
      return (tags as unknown[])
        .filter((t): t is string => typeof t === 'string' && t.length > 1)
        .map((t: string) => t.replace(/^#+/, '').trim())
        .filter((t: string) => Boolean(t))
        .slice(0, 8);
    } catch {
      return [];
    }
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MeetingNotes {
  samenvatting: string;
  bulletPoints: string[];
  actiepunten: string[];
  beslissingen: string[];
  leerpunten: string[];
  score: number;
}

// Singleton instance
export const openAI = new OpenAIService();



